import React, { Fragment, useState } from 'react';

import './HelpedPerson.scss';

import { Button } from '../../../../components/Button/Button';
import { ContentBlock } from '../../../../components/ContentBlock/ContentBlock';

import editIcon from 'assets/images/icons/edit.svg';

import { FileWidget } from 'components/FileUploader/FileWidget/FileWidget';
import { useHelpedPersonVM } from './useHelpedPersonVM';
import { Document } from 'services/document/document.types';
import { ApiFile } from 'utils/useFlowTs';
import { mdphFormatter, statusMap } from 'utils/enumFormatter';
import { InformativeReunionStatus, Person } from 'services/person/person.types';
import Select from 'react-select';
import sanitizeHtml from 'sanitize-html';
import { formatFullDate } from 'utils/dates';

export default function HelpedPerson({ person, setPerson }: { person: Person; setPerson: (person: Person) => void }) {
    const {
        datalists,
        navigate,
        handleUploadDocument,
        handleUpdateDocument,
        handleDeleteDocument,
        personManagersOptions,
        handleUpdateResponsiblePerson
    } = useHelpedPersonVM(person, setPerson);

    if (!person) {
        return <>Personne non trouvée</>;
    }

    if (!datalists) {
        return <>Error</>;
    }

    const statusMap: Record<InformativeReunionStatus, string> = {
        pending: 'En attente',
        waiting: 'Souhaite réfléchir',
        no_followup: 'Sans suite'
    };

    const fieldsLabels: { [key: string]: string } = {
        firstname: 'Prénom',
        lastname: 'Nom',
        phone: 'Tél',
        genre: 'Genre',
        email: 'Adresse mail',
        address: 'Adresse postale',
        born_date: 'Date de naissance',
        department: 'Département'
    };

    function renderProfile(fieldToDisplay: string[] = ['firstname', 'lastname', 'phone', 'address', 'email', 'born_date', 'department']) {
        if (!person) return null;
        return Object.entries(person).map(([key, val]) => {
            if (!fieldToDisplay.includes(key) || !val) return false;

            let label = fieldsLabels[key];

            if (typeof val === 'object' && 'address' in val) {
                return (
                    <Fragment key={key}>
                        <p>{label}</p>
                        <p>{`${val.formatted}`}</p>
                    </Fragment>
                );
            }

            return (
                <Fragment key={key}>
                    <p>{label}</p>
                    <p>{String(val)}</p>
                </Fragment>
            );
        });
    }

    return (
        <section className="helped-person">
            <div className="heading">
                <Button
                    label="Modifier les informations"
                    variant="light"
                    icon={<img src={editIcon} alt="" />}
                    iconBefore
                    callback={() => {
                        navigate(`/app/admin/persons/${person.id}/edit`);
                    }}
                />
                <Select
                    options={personManagersOptions}
                    defaultValue={
                        person.responsible_person
                            ? {
                                  value: person.responsible_person?.id,
                                  label: person.responsible_person?.fullname
                              }
                            : {
                                  value: null,
                                  label: 'Aucun appv attribué'
                              }
                    }
                    onChange={handleUpdateResponsiblePerson}
                />
            </div>

            <div className="container">
                <ContentBlock title="Profil" className="profile">
                    <p className="subtitle">Personne soutenue</p>
                    <div className="infos">{renderProfile()}</div>
                    <div className="infos">
                        <Fragment key="user.privacy_policy_read">
                            <p>Charte de coopération lue</p>
                        </Fragment>
                        <Fragment key="user.privacy_policy_read">
                            <p>
                                {person?.user?.privacy_policy_read
                                    ? person?.user?.privacy_policy_accepted_at
                                        ? formatFullDate(person?.user?.privacy_policy_accepted_at)
                                        : 'Oui'
                                    : 'Non'}
                            </p>
                        </Fragment>
                    </div>
                    {person.source && (
                        <>
                            <span className="separator"></span>
                            <p className="subtitle">Source du contact</p>
                            <p>{datalists['person_sources'][person.source] ?? 'Inconnu'}</p>
                        </>
                    )}
                    {person.observation && (
                        <>
                            <span className="separator"></span>
                            <p className="subtitle">Commentaire</p>
                            <div dangerouslySetInnerHTML={{ __html: person.observation }} />
                        </>
                    )}
                </ContentBlock>

                {person.user?.personal_comment && (
                    <ContentBlock title="Commentaire de la personne soutenue" postIt>
                        <p style={{ whiteSpace: 'pre-wrap' }}>{person.user.personal_comment}</p>
                    </ContentBlock>
                )}

                <ContentBlock title="GED">
                    <FileWidget
                        documents={person.documents as unknown as ApiFile[]}
                        onAddFile={(file) => handleUploadDocument(file as unknown as Document)}
                        onUpdateFile={(file) => handleUpdateDocument(file as unknown as Document)}
                        onDeleteFile={(file) => handleDeleteDocument(file as unknown as Document)}
                    />
                </ContentBlock>
                <ContentBlock title="Réunion informative">
                    <div className="infos">
                        {person.informative_reunion?.done ? (
                            <>
                                <p className="subtitle">Date de la réunion</p>
                                <p>
                                    {person.informative_reunion?.date
                                        ? new Date(person.informative_reunion.date).toLocaleDateString('fr-FR')
                                        : 'Non renseignée'}
                                </p>
                                <p className="subtitle">Intervenat ayant réalisé la réunion</p>
                                <p>{person.informative_reunion?.supervisor?.fullname || 'Non renseignée'}</p>
                                <p className="subtitle">Lieu de la réunion</p>
                                <p>{person.informative_reunion?.location || 'Non renseigné'}</p>
                                <p className="subtitle">Département de la réunion</p>
                                <p>{person.informative_reunion?.department || 'Non renseigné'}</p>
                                <p className="subtitle">Statut de la personne soutenue</p>
                                <p>{person.informative_reunion?.status && statusMap[person.informative_reunion.status]}</p>
                            </>
                        ) : (
                            <p>
                                <b>Réunion informative non réalisée</b>
                            </p>
                        )}
                    </div>
                </ContentBlock>
                <ContentBlock title="Caractéristiques" className="caracteristics">
                    <div className="infos">
                        <p className="subtitle">Principal</p>
                        {person.disorders?.main ? (
                            <>
                                <p>{datalists['disorders'][person.disorders.main]}</p>
                            </>
                        ) : (
                            <>Aucune caractéristique principale enregistrée</>
                        )}
                        <p className="subtitle">Secondaires</p>
                        {person.disorders?.secondaries?.length ? (
                            <>
                                {person.disorders.secondaries.map((dis, index) => (
                                    <span key={`secondary-disorder-${index}`}>
                                        {datalists['disorders'][dis] + (index !== person.disorders.secondaries.length - 1 ? ', ' : '')}
                                    </span>
                                ))}
                            </>
                        ) : (
                            <>Aucune caractéristique secondaire enregistrée</>
                        )}

                        <p className="subtitle">Reconnaissance MDPH</p>
                        {person.disorders?.mdph ? (
                            <>
                                <p>{mdphFormatter(person.disorders.mdph)}</p>
                            </>
                        ) : (
                            <>Non renseigné</>
                        )}
                    </div>
                </ContentBlock>
                {person.projects?.length > 0 && (
                    <ContentBlock title="Demande initiale du projet" className="initial-request">
                        <p dangerouslySetInnerHTML={{ __html: sanitizeHtml(person.projects?.[0].initial_request) }} />
                        {person.projects?.[0]?.user_initial_request_comment && (
                            <>
                                <span className="separator"></span>
                                <p className="subtitle">Commentaire de la personne soutenue</p>
                                <p style={{ whiteSpace: 'pre-wrap' }}>{person.projects[0].user_initial_request_comment}</p>
                            </>
                        )}
                    </ContentBlock>
                )}
            </div>
        </section>
    );
}
